import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import {
  NSC_CONSENTS_LAST_SAVED_DATE,
  NSC_CONSENTS_LAST_SAVED_LANGUAGE,
  NSCConsent,
  NSCConsentChoice,
  NSCConsentData,
  NSCConsentSelection,
  NSCConsentUsage,
  NSCConsentVariant,
} from '../../../model/nsc-consent';
import { ConsentLegalText, RootTextBlock, TextBlock } from '../../../model/nsc-consent-legal-text';
import { PreregistrationData } from '../../../model/preregistration-data';
import { ConsentRequestValue, PreregistrationRequest, SetConsentChoicesRequest } from '../model/request.model';
import {
  GcdmConsents,
  GcdmConsentState,
  GcdmConsentWithDate,
  PolicyDocument,
  PolicyDocumentTextBlock,
  PolicyDocumentUsageMimeType,
  PolicyDocumentUsageTag,
  PolicyDocumentUsageText,
  PolicyDocumentVariant,
} from '../model/response.model';

interface PolicyDocumentRootTextBlock {
  textBlock: PolicyDocumentUsageText;
  popupTextBlock?: PolicyDocumentUsageText;
}

/**
 * A class for converting between GCDM requests/responses and the view model
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmViewModelMapper {
  private readonly logger = inject(NGXLogger);

  policyDocumentToConsent(policyDocument: PolicyDocument): NSCConsentData {
    return {
      date: new Date(policyDocument.validFrom),
      majorVersion: policyDocument.majorVersion,
      minorVersion: policyDocument.minorVersion,
      languages: new Map(
        policyDocument.textBlocks.map((policyDocumentTextBlock) => [
          policyDocumentTextBlock.language,
          {
            consents: this.extractNscConsents(policyDocument, policyDocumentTextBlock),
            legalText: this.extractConsentLegalText(policyDocumentTextBlock),
          },
        ]),
      ),
    };
  }

  private extractConsentLegalText(textsForLanguage: PolicyDocumentTextBlock): ConsentLegalText {
    const texts: ConsentLegalText = {};
    for (const { textBlock, popupTextBlock } of this.usagesWithPopups(textsForLanguage)) {
      const rootTextBlock = this.makeRootTextBlock(textBlock, popupTextBlock);
      switch (textBlock.tag) {
        case PolicyDocumentUsageTag.HEADER:
        case PolicyDocumentUsageTag.AFTER_USAGE_DEF:
        case PolicyDocumentUsageTag.BEFORE_USAGE_DEF: {
          texts[textBlock.tag] = rootTextBlock;
          break;
        }
        case PolicyDocumentUsageTag.POPUP: {
          this.logger.error(
            '#CDFTF0035 Popups should not be encountered in extractConsentText, usagesWithPopups should filter them out',
          );
          break;
        }
        default: {
          this.logger.warn(
            '#CDFTF0034 Unexpected tag type encountered in legal text response from GCDM',
            textBlock.tag,
          );
        }
      }
    }
    return texts;
  }

  /** Construct a root text block, possibly also including a nested text block as a popup */
  private makeRootTextBlock(usageText: PolicyDocumentUsageText, popupText?: PolicyDocumentUsageText): RootTextBlock {
    return {
      ...this.makeTextBlock(usageText),
      popup: popupText && this.makeTextBlock(popupText),
    };
  }

  /** Construct a text block, either at the root or as a popup */
  makeTextBlock(usageText: PolicyDocumentUsageText): TextBlock {
    return {
      text: usageText.value,
      isHtml: usageText.mimeType === PolicyDocumentUsageMimeType.HTML,
    };
  }

  makeTextBlockFromMimeType(text: string | undefined, mimeType: string | undefined): TextBlock | undefined {
    if (text === undefined) {
      return undefined;
    }
    return {
      text: text,
      isHtml: mimeType === PolicyDocumentUsageMimeType.HTML,
    };
  }

  /**
   * Iterate through the text, grouping each node with the immediately following popup, if present.
   *
   * Any other popups are ignored.
   */
  private *usagesWithPopups(textsForLanguage: PolicyDocumentTextBlock): Generator<PolicyDocumentRootTextBlock> {
    const textBlocks = textsForLanguage.usageText ?? [];

    for (let i = 0; i < textBlocks.length; i++) {
      const usageText = textBlocks[i];
      if (usageText.tag === PolicyDocumentUsageTag.POPUP) continue;

      if (textBlocks[i + 1]?.tag === PolicyDocumentUsageTag.POPUP) {
        const popupText = textBlocks[i + 1];
        yield { textBlock: usageText, popupTextBlock: popupText };
      } else {
        yield { textBlock: usageText };
      }
    }
  }

  private extractNscConsents(
    policyDocument: PolicyDocument,
    policyDocumentTextBlock: PolicyDocumentTextBlock,
  ): NSCConsent[] {
    return (
      policyDocumentTextBlock.usageDefinitions?.map(
        (policyDocumentUsageDefinition): NSCConsent => ({
          policyId: policyDocument.name,
          majorVersion: policyDocument.majorVersion,
          minorVersion: policyDocument.minorVersion,
          brand: policyDocument.brand,
          language: policyDocumentTextBlock.language,
          usageDefinitionId: String(policyDocumentUsageDefinition.id),
          usageDefinitionPurposes: policyDocumentUsageDefinition.purposes.map((purpose) => purpose.purpose.name),
          textPrimary: this.makeTextBlockFromMimeType(
            policyDocumentUsageDefinition.textPrimary,
            policyDocumentUsageDefinition.mimeTypeTextPrimary,
          ),
          textSecondary: this.makeTextBlockFromMimeType(
            policyDocumentUsageDefinition.textSecondary,
            policyDocumentUsageDefinition.mimeTypeTextSecondary,
          ),
          usages: policyDocumentUsageDefinition.usages.map(
            (usage): NSCConsentUsage => ({
              id: usage.usage.id,
              name: usage.usage.name,
              label: usage.usage.label ?? '',
              usageVariant: GcdmViewModelMapper.variantMap[usage.variant],
            }),
          ),
        }),
      ) ?? []
    );
  }

  private static variantMap: Record<PolicyDocumentVariant, NSCConsentVariant> = {
    [PolicyDocumentVariant.IMPLICIT]: NSCConsentVariant.IMPLICIT,
    [PolicyDocumentVariant.MANDATORY]: NSCConsentVariant.MANDATORY,
    [PolicyDocumentVariant.OPTIONAL]: NSCConsentVariant.OPTIONAL,
  };

  mapNscConsentChoices(consentChoices: NSCConsentChoice[]): SetConsentChoicesRequest[] {
    // The consent date must be exactly the same for all entries in the request
    const consentDate = new Date().toISOString();
    return consentChoices.flatMap((consentChoice) =>
      consentChoice.nscConsent.usageDefinitionPurposes.map(
        (purposeId): SetConsentChoicesRequest => ({
          consent: consentChoice.selected ? ConsentRequestValue.ACCEPT : ConsentRequestValue.REJECT,
          brand: consentChoice.nscConsent.brand,
          language: consentChoice.nscConsent.language,
          usageId: consentChoice.nscConsentUsage.name,
          majorVersion: consentChoice.nscConsent.majorVersion,
          minorVersion: consentChoice.nscConsent.minorVersion,
          policyId: consentChoice.nscConsent.policyId,
          consentDate,
          purposeId,
        }),
      ),
    );
  }

  unwrapConsentChoices(consentsResponse: GcdmConsents, policyId: string): NSCConsentSelection {
    const consentChoices: Record<string, GcdmConsentWithDate> = {};
    let lastConsentDate: Date | undefined = undefined;
    let lastConsentLanguage: string | undefined = undefined;
    for (const consent of consentsResponse) {
      if (consent.policyId !== policyId) continue;
      const parsedConsentDate = new Date(consent.consentDate);
      const existing: GcdmConsentWithDate | undefined = consentChoices[consent.usageId];
      if (!existing || existing.parsedConsentDate < parsedConsentDate) {
        consentChoices[consent.usageId] = { ...consent, parsedConsentDate };
      }
      if (!lastConsentDate || lastConsentDate < parsedConsentDate) {
        lastConsentDate = parsedConsentDate;
        lastConsentLanguage = consent.language;
      }
    }
    const selection = Object.fromEntries(
      Object.entries(consentChoices).map(([usage, consent]) => [usage, consent.consent === GcdmConsentState.ACCEPT]),
    );
    return {
      ...selection,
      [NSC_CONSENTS_LAST_SAVED_DATE]: lastConsentDate,
      [NSC_CONSENTS_LAST_SAVED_LANGUAGE]: lastConsentLanguage,
    };
  }

  mapPreregistrationData(data: PreregistrationData): PreregistrationRequest {
    return {
      brand: data.brand,
      emailAddress: data.emailAddress,
      gcid: data.gcid,
      language: data.language,
    };
  }
}
